import type { ArsColorCode, BarDiagramData, PhaseSlot, GroupRow, PlanInfo, DiagramMetadata } from '../types/barDiagram';

const VALID_COLORS = new Set<string>([
  'p0','p1','p2','p3','p4','p5','p6','p7','p8','p9',
  'p10','p11','p14','p15','p16','p22','p39','-',
]);

function sanitizeColor(raw: string): ArsColorCode {
  const trimmed = raw.trim();
  if (VALID_COLORS.has(trimmed)) return trimmed as ArsColorCode;
  return '-';
}

/**
 * Parsea un HTML generado por ARS_93 y extrae los datos del diagrama de barras.
 *
 * Estructura esperada del HTML:
 * - Tabla principal con border='1' que contiene:
 *   - Fila 0: Header con labels (Dsf, F1, t1, t2, F2, ...) y planes/desfases
 *   - Fila 1: T(seg) con duraciones en inputs
 *   - Filas 2-17: G1-G16 con celdas coloreadas (class='pX')
 * - Tabla footer con metadata (Localitat, Projecte, etc.)
 */
export function parseArsHtml(htmlString: string): BarDiagramData {
  const parser = new DOMParser();
  const doc = parser.parseFromString(htmlString, 'text/html');

  // Buscar la tabla principal del diagrama (table con border='1')
  const tables = doc.querySelectorAll('table[border="1"]');
  const mainTable = tables[0];
  if (!mainTable) throw new Error('No se encontró la tabla principal del diagrama');

  const rows = mainTable.querySelectorAll('tr');

  // --- Header (fila 0): extraer labels y planes ---
  const headerRow = rows[0];
  const headerCells = headerRow.querySelectorAll('th');

  const phases: PhaseSlot[] = [];

  // Primera celda (Dsf.) contiene desfases en <br>-separated text
  const dsfCell = headerCells[0];
  const dsfText = dsfCell?.innerHTML || '';
  // Extraer desfases: "Dsf.<br><br>21<br>1<br>73<br>..."
  const dsfParts = dsfText.split(/<br\s*\/?>/i).map(s => s.trim()).filter(Boolean);
  // dsfParts[0] = "Dsf.", resto son offsets de planes
  const offsets = dsfParts.slice(1).map(s => parseInt(s, 10)).filter(n => !isNaN(n));

  // Temporalmente almacenar duraciones por columna: columnDurations[colIdx] = [dur_plan0, dur_plan1, ...]
  const columnDurations: number[][] = [];

  // Resto de celdas del header: labels (F1, t1, t2, F2, ...) + duraciones por plan
  const numColumns = headerCells.length - 1;
  for (let i = 1; i < headerCells.length; i++) {
    const colIdx = i - 1;
    const cellHtml = headerCells[i]?.innerHTML || '';
    const parts = cellHtml.split(/<br\s*\/?>/i).map(s => s.trim()).filter(Boolean);
    const label = parts[0] || '';
    const isPhase = label.startsWith('F');
    const durations = parts.slice(1).map(s => parseInt(s, 10)).filter(n => !isNaN(n));

    phases.push({
      label,
      duration: 0, // se llenará con la fila T(seg)
      type: isPhase ? 'phase' : 'transition',
    });

    columnDurations[colIdx] = durations; // puede ser [] para transiciones
  }

  // Construir plans: cada plan tiene phaseDurations[colIdx] = valor o null
  const numPlans = Math.max(offsets.length, ...columnDurations.map(d => d.length));
  const plans: PlanInfo[] = [];
  for (let p = 0; p < numPlans; p++) {
    const phaseDurations: (number | null)[] = [];
    for (let c = 0; c < numColumns; c++) {
      const val = columnDurations[c]?.[p];
      phaseDurations.push(val !== undefined ? val : null);
    }
    plans.push({ offset: offsets[p] ?? 0, rawOffset: offsets[p] ?? 0, phaseDurations });
  }

  // --- Fila T(seg) (fila 1): duraciones de cada phase slot ---
  const tsegRow = rows[1];
  if (tsegRow) {
    const tsegCells = tsegRow.querySelectorAll('td');
    tsegCells.forEach((td, idx) => {
      const input = td.querySelector('input');
      const val = parseInt(input?.getAttribute('value') || '0', 10);
      if (idx < phases.length) {
        phases[idx].duration = isNaN(val) ? 0 : val;
      }
    });
  }

  // --- Filas G1-G16 (filas 2-17): colores de cada grupo ---
  const groups: GroupRow[] = [];
  for (let r = 2; r < rows.length && groups.length < 16; r++) {
    const row = rows[r];
    const th = row.querySelector('th');
    const label = th?.textContent?.trim() || `G${groups.length + 1}`;
    const tds = row.querySelectorAll('td');
    const cells: ArsColorCode[] = [];

    tds.forEach(td => {
      // El color viene en el class del <td> o en el value del <input> hidden
      const input = td.querySelector('input');
      const colorFromInput = input?.getAttribute('value')?.trim() || '';
      const colorFromClass = td.getAttribute('class')?.trim() || '';

      // Preferir el class del td, fallback al value del input
      const raw = colorFromClass || colorFromInput;
      cells.push(sanitizeColor(raw));
    });

    groups.push({
      id: groups.length + 1,
      label,
      cells,
    });
  }

  // --- Metadata del footer ---
  const metadata = parseMetadata(doc);

  return { metadata, phases, groups, plans };
}

function parseMetadata(doc: Document): DiagramMetadata {
  const meta: DiagramMetadata = {
    cruilla: '',
    localitat: '',
    projecte: '',
    data: '',
    regulador: '',
    planol: '',
    modificat: '',
  };

  // Extraer número de cruce del título "DIAGRAMA BARRAS: 028"
  const titulo = doc.querySelector('.Titulo');
  if (titulo) {
    const match = titulo.textContent?.match(/(\d+)/);
    if (match) meta.cruilla = match[1];
  }

  // Buscar la tabla de metadata (segunda tabla del body o tabla con class Tabla2)
  const tabla2 = doc.querySelector('.Tabla2');
  if (!tabla2) return meta;

  const allTh = tabla2.querySelectorAll('th');
  for (let i = 0; i < allTh.length; i++) {
    const text = allTh[i].textContent?.trim() || '';

    if (text === 'Localitat:') {
      meta.localitat = allTh[i + 1]?.textContent?.trim() || '';
    } else if (text === 'N.Cruilla:') {
      // El valor está en la siguiente celda con contenido tras el colspan
      // En el HTML de ARS_93, el N.Cruilla está en la misma fila que Localitat
      // pero el valor se pone en una celda posterior
      const nextRow = allTh[i]?.closest('tr');
      if (nextRow) {
        const ths = nextRow.querySelectorAll('th');
        const lastTh = ths[ths.length - 1];
        if (lastTh && lastTh.textContent?.trim() !== 'N.Cruilla:') {
          // cruilla ya extraída del título
        }
      }
    } else if (text === 'Projecte:') {
      meta.projecte = allTh[i + 1]?.textContent?.trim() || '';
    } else if (text === 'Data:') {
      meta.data = allTh[i + 1]?.textContent?.trim() || '';
    } else if (text === 'Regulador:') {
      meta.regulador = allTh[i + 1]?.textContent?.trim() || '';
    } else if (text === 'N. Planol:') {
      meta.planol = allTh[i + 1]?.textContent?.trim() || '';
    } else if (text === 'Modificat:') {
      meta.modificat = allTh[i + 1]?.textContent?.trim() || '';
    }
  }

  // Fallback: buscar cruilla en el diagrama description
  if (!meta.cruilla) {
    const allPs = tabla2.querySelectorAll('p');
    for (const p of allPs) {
      const match = p.textContent?.trim().match(/^(\d{2,4})$/);
      if (match) {
        meta.cruilla = match[1];
        break;
      }
    }
  }

  return meta;
}
